import { useCallback, useMemo } from 'react';

import { Folders, UploadedFile } from '../../../@types/uploaded-files.type';
import { useGetUploadedFiles } from '../../../queries/uploadedfiles-query';
import { isSafeUrl } from '../../../utils/common';
import { InteractionFileValue } from '../../../utils/interaction-note-format';
import { ViewerImage } from '../AttachmentsPanel/ImageViewer';

/** An interaction file answer matched to the actual attachment on the job. */
export interface ResolvedInteractionFile {
  url: string;
  fileName: string;
  isImage: boolean;
  /** The attachment's own id, which the mobile viewer needs to offer a download. */
  id: number;
}

/**
 * The storage key for a service attachment is `<folder>/<timestamp> -<fileName>`, so
 * the uploaded name has to be recovered from the tail rather than the whole key.
 * Mirrors the same parse in `useLogFileViewer`.
 */
const fileNameFromKey = (key: string): string => {
  const separator = key.indexOf(' -');
  return (separator >= 0 ? key.slice(separator + 2) : key.split('/').pop() || key).trim();
};

const normalise = (fileName: string): string => fileName.trim().toLowerCase();

/** One of the job's image attachments, ready for either platform's viewer. */
export type JobImage = ViewerImage & { id: number; key?: string };

/**
 * Every image attached to the job, in list order, as the shared viewer wants them —
 * plus the position of a given storage key within that set.
 *
 * This is what turns a single file link in a note into something browsable: clicking
 * one photo opens the viewer on it, and its arrows and dots then move through the
 * rest of the job's images instead of dead-ending on the one that was clicked.
 */
export const useJobImageAttachments = (serviceId?: number) => {
  const { data } = useGetUploadedFiles(serviceId ?? 0, Folders.ServiceAttachments);

  return useMemo(() => {
    const files = (data ?? []).filter(
      (file) => file.mimeType?.startsWith('image/') && file.url && isSafeUrl(file.url)
    );
    // `id` and `key` ride along for the mobile viewer, which identifies an image by
    // attachment id to offer a download. The desktop viewer ignores them.
    const images: JobImage[] = files.map((file) => ({
      id: file.id,
      key: file.key,
      url: file.url,
      fileName: file.fileName || (file.key ? fileNameFromKey(file.key) : 'Image'),
    }));
    const indexOfKey = (key: string): number => files.findIndex((file) => file.key === key);
    return { images, indexOfKey };
  }, [data]);
};

/**
 * Resolving an interaction's file answers to something viewable.
 *
 * A photo uploaded through an interaction is a real service attachment — the field
 * answer only references it. The reference differs by surface: mobile keeps the
 * `fileKey` from the upload response, desktop keeps just the file name. Both are
 * matched here against the job's attachment list, which already carries a presigned
 * `url` and the `mimeType`, so no extra request is needed per photo.
 *
 * The list is fetched with the same query key the Attachments panel uses, so on the
 * service detail page this is a cache read rather than a second fetch.
 */
export const useInteractionAttachments = (serviceId?: number) => {
  const { data } = useGetUploadedFiles(serviceId ?? 0, Folders.ServiceAttachments);

  const { byKey, byName } = useMemo(() => {
    const keyed = new Map<string, UploadedFile>();
    const named = new Map<string, UploadedFile>();
    for (const file of data ?? []) {
      if (file.key) {
        keyed.set(file.key, file);
        // Two attachments can share a display name; the earliest wins so the mapping
        // is stable across renders rather than depending on list order changes.
        const fromKey = normalise(fileNameFromKey(file.key));
        if (fromKey && !named.has(fromKey)) named.set(fromKey, file);
      }
      const name = normalise(file.fileName ?? '');
      if (name && !named.has(name)) named.set(name, file);
    }
    return { byKey: keyed, byName: named };
  }, [data]);

  /**
   * Returns the matched attachment, or undefined when the answer references a file
   * that is no longer on the job (deleted, or uploaded against a different entity).
   * Callers must handle undefined by showing the file name as plain text.
   */
  return useCallback(
    (file: InteractionFileValue): ResolvedInteractionFile | undefined => {
      // When the answer carried a key, that key is the answer — no name fallback. A
      // stale key whose file name happens to match a *different* attachment would
      // otherwise resolve to the wrong image, which is worse than resolving to none.
      const match = file.fileKey ? byKey.get(file.fileKey) : byName.get(normalise(file.fileName));
      // The url is presigned by the API; an unexpected scheme must never reach an
      // <img src> or window.open.
      if (!match?.url || !isSafeUrl(match.url)) return undefined;
      return {
        url: match.url,
        fileName: file.fileName || match.fileName,
        isImage: !!match.mimeType?.startsWith('image/'),
        id: match.id,
      };
    },
    [byKey, byName]
  );
};
